import {
    DefaultButton,
    DefaultEffects,
    Dialog,
    DialogFooter,
    DialogType,
    IContextualMenuProps,
    Label,
    Panel,
    PrimaryButton,
    SearchBox,
    Stack,
    Text,
    TextField,
    getTheme,
    mergeStyles
} from '@fluentui/react';
import { useOrganizationBranding } from 'features/Header/hooks';
import { IResource } from 'kits/apiKit3/legacy';
import { getTextColorForBackground, IvicosColors } from 'kits/colorKit';
import IvicosStrings from 'kits/language/stringKit';
import React, { useMemo, useState } from 'react';
import { IIdentityClaims } from 'shared-state/identity/types';
import { useCurrentOrgId } from 'shared-state/location/hook';
import { getClassNames } from 'styles/responsiveness';
import AreaCard from '../AreaCard/AreaCard';
import { useWindowWidth } from 'shared-state/display-options/hooks';
import { useAreaDirectoryLoading, useCurrentOrgBranding } from 'shared-state/directory/hooks';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import CreationAnimationLoading from './component/CreationAnimationLoading/CreationAnimationLoading';
import AllAreasDeleted from './component/AllAreasDeleted/AllAreasDeleted';
import { useFilterAreasAndAreaGroups } from './component/filterAreas';
import { IC } from 'components/SVGIcon';
import { EnterOffice, LevelElevatorPolygon } from 'kits/IconKit2';
import { isValidAreaSortNumber, parseStrictIntegerString } from './sortNumberValidation';

function areaPanelErrorMessage(err: unknown): string {
    if (typeof err === 'object' && err !== null && 'response' in err) {
        const data = (err as { response?: { data?: unknown } }).response?.data;
        if (typeof data === 'string' && data.length > 0) return data;
        if (typeof data === 'object' && data !== null && 'message' in data && typeof (data as { message: unknown }).message === 'string') {
            return (data as { message: string }).message;
        }
    }
    if (err instanceof Error) return err.message;
    return String(err);
}

export interface IAreaListViewProps {
    areas: {
        isStared: boolean;
        id: string;
        type: string;
        attributes: any;
        relationships: any;
    }[];
    areaGroups: {
        isStared: boolean;
        id: string;
        type: string;
        attributes: any;
        relationships: any;
    }[];
    vertical?: boolean;
    panelHeader: string;
    modalIsOpen: boolean;
    onAreaSelected: (areaId: string) => void;
    staredAreas: string[];
    onAreaStared: (a: IResource, s: boolean) => void;
    campusId?: string;
    roomId?: string;
    isPanelOpen: boolean;
    openPanel: () => void;
    dismissPanel: () => void;
    setPanelHeader: React.Dispatch<React.SetStateAction<string>>;
    areaName: string;
    setAreaName: React.Dispatch<React.SetStateAction<string>>;
    areaTag: string;
    setAreaTag: React.Dispatch<React.SetStateAction<string>>;
    areaSortNumber: number | undefined;
    setAreaSortNumber: React.Dispatch<React.SetStateAction<number | undefined>>;
    /** `undefined` create; `null` edit with no server sort; `number` snapshot at edit open. */
    sortNumberAtOpenSnapshot: number | null | undefined;
    seedAreaEditPanel: (areaId: string, areaResource: any, explicit?: { name?: string; tag?: string; sortNumber?: number }) => void;
    setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    deleteAreaOrAreaGroup: { id: string; name: string; parentAreaId?: string; isAreaGroup?: boolean };
    searchArea: (e: any, newValue: any) => void;
    searchInput: string;
    identityClaims?: IIdentityClaims;
    deleteAreaHandler: (deleteAreaOrAreaGroup: { id: string; name: string; parentAreaId?: string; isAreaGroup?: boolean }) => Promise<void>;
    onCreateAreaHandler: () => Promise<boolean>;
    onAreaChangeHandler: () => Promise<boolean>;
    onDismiss: () => void;
    areaMenuProps: IContextualMenuProps;
    isAddAreaGroupSelected: boolean;
    setIsAddAreaGroupSelected: React.Dispatch<React.SetStateAction<boolean>>;
    isCreatingOrEditingAreaOrGroup: boolean;
}

const AreaListView: React.FC<IAreaListViewProps> = ({
    areas,
    areaGroups,
    onAreaSelected,
    vertical,
    onAreaStared,
    campusId,
    isPanelOpen,
    openPanel,
    dismissPanel,
    panelHeader,
    setPanelHeader,
    areaName,
    setAreaName,
    areaTag,
    setAreaTag,
    areaSortNumber,
    setAreaSortNumber,
    sortNumberAtOpenSnapshot,
    seedAreaEditPanel,
    modalIsOpen,
    setModalIsOpen,
    deleteAreaOrAreaGroup,
    searchArea,
    searchInput,
    identityClaims,
    deleteAreaHandler,
    onCreateAreaHandler,
    onAreaChangeHandler,
    onDismiss,
    areaMenuProps,
    isAddAreaGroupSelected,
    setIsAddAreaGroupSelected,
    isCreatingOrEditingAreaOrGroup
}) => {
    const { groupLevel } = useParams<{ groupLevel?: string }>();
    const [orgId] = useCurrentOrgId();
    const [branding] = useOrganizationBranding(orgId);
    const color = branding?.orgColors[0];
    const theme = getTheme();
    const { smallDevice, mediumDevice } = useWindowWidth();
    const [isHovered, setIsHovered] = useState(false);
    const [isAreaDirectoryLoading] = useAreaDirectoryLoading();

    const filterAreas = useFilterAreasAndAreaGroups(areas, areaGroups, searchInput);

    const sortNumberFieldError = useMemo(() => {
        if (sortNumberAtOpenSnapshot === null) {
            if (areaSortNumber === undefined) return undefined;
            if (!isValidAreaSortNumber(areaSortNumber)) return IvicosStrings.enterAreaSortNumberError;
            return undefined;
        }
        if (areaSortNumber === undefined || !isValidAreaSortNumber(areaSortNumber)) {
            return IvicosStrings.enterAreaSortNumberError;
        }
        return undefined;
    }, [areaSortNumber, sortNumberAtOpenSnapshot]);

    const sortPanelCommitDisabled = Boolean(sortNumberFieldError);

    // Responsive design
    const { areasSearchBar, areasSearchBarVertical } = getClassNames();

    const dialogContentProps = {
        type: DialogType.normal,
        title: deleteAreaOrAreaGroup.isAreaGroup ? IvicosStrings.deleteAreaGroupActionTitle : IvicosStrings.deleteAreaActionTitle,
        subText: `${IvicosStrings.deleteAreaDescription} ${deleteAreaOrAreaGroup.name}?`
    };

    const modelProps = {
        isBlocking: true,
        topOffsetFixed: false
    };
    const [orgBranding] = useCurrentOrgBranding();
    const getPrimaryCardColor = () => orgBranding?.orgColors[0] || IvicosColors.Mittelblau;

    const areaCardImageStyles = mergeStyles({
        borderRadius: 2,
        height: 'auto',
        minHeight: 48,
        width: 'auto',
        maxWidth: smallDevice ? 230 : 260,
        padding: '4px 8px',
        border: '2px #fff solid',
        boxShadow: DefaultEffects.elevation8,
        color: theme.palette.neutralLighter,
        background: getPrimaryCardColor(),
        marginTop: -48,
        marginBottom: 16
    });

    const onRenderFooterContent = React.useCallback(
        () => (
            <Stack tokens={{ childrenGap: 15 }} horizontal aria-label="buttons-container">
                {panelHeader === IvicosStrings.createAreaTitle || panelHeader === IvicosStrings.createAreaGroupTitle ? (
                    <PrimaryButton
                        style={{ width: 110 }}
                        text={isCreatingOrEditingAreaOrGroup ? IvicosStrings.creatingActionLoadingTitle : IvicosStrings.confirmActionTitle}
                        disabled={sortPanelCommitDisabled || isCreatingOrEditingAreaOrGroup}
                        onClick={async () => {
                            if (sortPanelCommitDisabled || isCreatingOrEditingAreaOrGroup) return;
                            try {
                                const ok = await onCreateAreaHandler();
                                if (ok) dismissPanel();
                            } catch (err) {
                                await Swal.fire({
                                    icon: 'error',
                                    title: IvicosStrings.errorOccurred,
                                    text: areaPanelErrorMessage(err)
                                });
                            }
                        }}
                        aria-label="confirm-button"
                    />
                ) : (
                    <PrimaryButton
                        style={{ width: 110 }}
                        text={isCreatingOrEditingAreaOrGroup ? IvicosStrings.savingActionLoadingTitle : IvicosStrings.saveActionTitle}
                        disabled={sortPanelCommitDisabled || isCreatingOrEditingAreaOrGroup}
                        onClick={async () => {
                            if (sortPanelCommitDisabled || isCreatingOrEditingAreaOrGroup) return;
                            try {
                                const ok = await onAreaChangeHandler();
                                if (ok) dismissPanel();
                            } catch (err) {
                                await Swal.fire({
                                    icon: 'error',
                                    title: IvicosStrings.errorOccurred,
                                    text: areaPanelErrorMessage(err)
                                });
                            }
                        }}
                        aria-label="confirm-button"
                    />
                )}
                <DefaultButton
                    text={IvicosStrings.cancelActionTitle}
                    style={{ marginRight: 10, width: 110 }}
                    onClick={() => {
                        dismissPanel();
                    }}
                    aria-label="cancel-button"
                />
            </Stack>
        ),
        [dismissPanel, isCreatingOrEditingAreaOrGroup, onAreaChangeHandler, onCreateAreaHandler, panelHeader, sortPanelCommitDisabled]
    );

    return (
        <>
            <Stack grow className={vertical ? '' : 'padding--large'} tokens={{ childrenGap: 16 }}>
                <Stack horizontalAlign="start" tokens={{ childrenGap: 16 }} horizontal>
                    {filterAreas.length > 0 && vertical && (
                        <Stack horizontal={false} style={{ width: '100%' }}>
                            <Label>{IvicosStrings.availableAreasTitle}</Label>
                            <SearchBox
                                data-test-id={vertical ? 'search-areas-input-vertical' : 'search-areas-input'} //doesn't detect aria-label
                                placeholder={IvicosStrings.searchAreasPlaceholder}
                                iconProps={{ iconName: 'search' }}
                                type="text"
                                onChange={searchArea}
                                value={searchInput}
                                className={vertical ? areasSearchBarVertical : areasSearchBar}
                            />
                        </Stack>
                    )}
                    {filterAreas.length > 0 && !vertical && (
                        <SearchBox
                            data-test-id={vertical ? 'search-areas-input-vertical' : 'search-areas-input'} //doesn't detect aria-label
                            placeholder={IvicosStrings.searchAreasPlaceholder}
                            iconProps={{ iconName: 'search' }}
                            type="text"
                            onChange={searchArea}
                            value={searchInput}
                            className={vertical ? areasSearchBarVertical : areasSearchBar}
                        />
                    )}

                    {(identityClaims?.roles.includes('manager') || identityClaims?.roles.includes('owner')) && !vertical && !smallDevice && !mediumDevice && (
                        <PrimaryButton
                            aria-label="add-area-button"
                            text={IvicosStrings.addAreaActionTitle}
                            iconProps={{ iconName: 'CircleAddition' }}
                            menuProps={areaMenuProps}
                        />
                    )}
                </Stack>
                <Stack
                    grow
                    style={{
                        position: 'relative',
                        height: vertical ? '100%' : '400px',
                        width: vertical ? '110%' : '100%',
                        alignSelf: 'center',
                        overflowY: 'auto',
                        paddingBottom: '200px',
                        backgroundColor:
                            smallDevice || mediumDevice || vertical
                                ? 'none'
                                : groupLevel === 'L0'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : groupLevel === 'L1'
                                ? 'rgba(255, 255, 255, 0.8)'
                                : 'rgba(255, 255, 255, 0.35)',
                        padding: smallDevice ? '0px' : '16px',
                        borderRadius: '4px',
                        boxShadow: smallDevice || mediumDevice || vertical ? 'none' : '0 0 10px 0 rgba(0, 0, 0, 0.1)'
                    }}
                >
                    {filterAreas.length === 0 ? (
                        <AllAreasDeleted
                            isEligibleToCreateAreas={
                                ((identityClaims?.roles.includes('manager') || identityClaims?.roles.includes('owner')) &&
                                    !vertical &&
                                    !smallDevice &&
                                    !mediumDevice) ||
                                false
                            }
                            isAreaDirectoryLoading={isAreaDirectoryLoading}
                            compact={!!vertical}
                        />
                    ) : (
                        <Stack
                            aria-label={vertical ? 'area-card-container-vertical' : 'area-cards-container'}
                            horizontal={!vertical}
                            verticalAlign="start"
                            wrap={!vertical}
                            style={{
                                paddingBottom: '100px'
                            }}
                        >
                            {filterAreas.map((area: any) => {
                                return (
                                    <AreaCard
                                        key={area.id}
                                        campusId={campusId}
                                        areaId={area.id}
                                        area={area}
                                        areaName={areaName}
                                        setAreaName={setAreaName}
                                        areaTag={areaTag}
                                        setAreaTag={setAreaTag}
                                        seedAreaEditPanel={(areaId: string, areaName?: string, areaTag?: string, sortNumber?: number) => {
                                            seedAreaEditPanel(areaId, area, {
                                                name: areaName,
                                                tag: areaTag,
                                                sortNumber
                                            });
                                        }}
                                        onClick={() => {
                                            onAreaSelected(area.id);
                                        }}
                                        onStar={(a, s) => onAreaStared(a, s)}
                                        modalIsOpen={modalIsOpen}
                                        setModalIsOpen={setModalIsOpen}
                                        fluid={vertical}
                                        panelHeader={panelHeader}
                                        setPanelHeader={setPanelHeader}
                                        isPanelOpen={isPanelOpen}
                                        openPanel={openPanel}
                                        dismissPanel={dismissPanel}
                                        vertical={vertical}
                                        setIsAddAreaGroupSelected={setIsAddAreaGroupSelected}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </Stack>

            {/* <Panel onRenderFooterContent={onRenderFooterContent} isFooterAtBottom={true} isOpen={true} headerText={panelHeader} onDismiss={dismissPanel}> */}
            <Panel
                onRenderFooterContent={onRenderFooterContent}
                isFooterAtBottom={true}
                isOpen={isPanelOpen}
                headerText={panelHeader}
                onDismiss={dismissPanel}
                aria-label="edit-create-area-panel"
            >
                <Stack aria-label="panel-create-area" horizontal={false} horizontalAlign="center" tokens={{ childrenGap: 15 }} style={{ marginTop: 21 }}>
                    <Stack style={{ width: '100%', marginBottom: 30 }} tokens={{ childrenGap: 10 }}>
                        <TextField
                            placeholder={isAddAreaGroupSelected ? IvicosStrings.enterAreaGroupTagPlaceholder : IvicosStrings.enterAreaTagPlaceholder}
                            label={isAddAreaGroupSelected ? IvicosStrings.enterAreaGroupTagLabel : IvicosStrings.enterAreaTagLabel}
                            type="text"
                            value={areaTag}
                            onChange={(e, newValue) => typeof newValue == 'string' && setAreaTag(newValue)}
                        />
                        <TextField
                            placeholder={isAddAreaGroupSelected ? IvicosStrings.enterAreaGroupNamePlaceholder : IvicosStrings.enterAreaNamePlaceholder}
                            label={isAddAreaGroupSelected ? IvicosStrings.enterAreaGroupNameLabel : IvicosStrings.enterAreaNameLabel}
                            type="text"
                            value={areaName}
                            onChange={(e, newValue) => typeof newValue == 'string' && setAreaName(newValue)}
                        />
                        <TextField
                            placeholder={
                                isAddAreaGroupSelected ? IvicosStrings.enterAreaGroupSortNumberPlaceholder : IvicosStrings.enterAreaSortNumberPlaceholder
                            }
                            label={isAddAreaGroupSelected ? IvicosStrings.enterAreaGroupSortNumberLabel : IvicosStrings.enterAreaSortNumberLabel}
                            type="number"
                            min={0}
                            max={1000}
                            value={areaSortNumber?.toString() ?? ''}
                            onChange={(e, newValue) => {
                                if (typeof newValue !== 'string') return;
                                setAreaSortNumber(parseStrictIntegerString(newValue));
                            }}
                            errorMessage={sortNumberFieldError}
                        />
                    </Stack>

                    {isCreatingOrEditingAreaOrGroup ? (
                        <CreationAnimationLoading />
                    ) : (
                        <Stack
                            styles={{
                                root: {
                                    width: '280px',
                                    height: 'auto',
                                    minHeight: '240px',
                                    marginTop: 20,
                                    marginBottom: 10,
                                    background: 'rgba(255, 255, 255, 0.3)',
                                    borderRadius: 2,
                                    border: '2px dotted #000'
                                }
                            }}
                        >
                            <Text
                                variant="large"
                                style={{ color: '#000', marginTop: -15, background: '#fff', width: 100, marginLeft: 10, textAlign: 'center' }}
                            >
                                {IvicosStrings.areaPreview}
                            </Text>
                            <Stack
                                styles={{
                                    root: {
                                        boxShadow: DefaultEffects.elevation16,
                                        position: 'relative',
                                        width: '245px',
                                        height: 'auto',
                                        minHeight: '200px',
                                        background: 'rgba(255, 255, 255, 0.3)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s ease',
                                        borderBottomRightRadius: isAddAreaGroupSelected ? 20 : 0,
                                        margin: 'auto',

                                        ':hover': {
                                            background: 'rgba(232,235,249,255)'
                                        }
                                    }
                                }}
                                aria-label="area-card"
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                            >
                                <Stack
                                    aria-label="area-color"
                                    key="area-color"
                                    horizontalAlign="end"
                                    verticalAlign="center"
                                    style={{ height: 32, background: getPrimaryCardColor() }}
                                    horizontal
                                ></Stack>
                                <Stack className="padding--default padding_vertical--large">
                                    <Stack key="area-image" verticalAlign="center" horizontalAlign="start" className={areaCardImageStyles} wrap>
                                        <Text variant="large" style={{ color: getTextColorForBackground(getPrimaryCardColor()), fontWeight: 'bold' }}>
                                            {areaTag || (isAddAreaGroupSelected ? IvicosStrings.enterAreaGroupTagLabel : IvicosStrings.enterAreaTagLabel)}
                                        </Text>
                                    </Stack>
                                    <Text variant="large" style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                                        {areaName || (isAddAreaGroupSelected ? IvicosStrings.enterAreaGroupNameLabel : IvicosStrings.enterAreaNameLabel)}
                                    </Text>
                                    <Stack
                                        verticalAlign="end"
                                        horizontalAlign="end"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            marginTop: 4,
                                            position: 'absolute',
                                            bottom: isAddAreaGroupSelected ? -1 : 5,
                                            right: isAddAreaGroupSelected ? -1 : 5,
                                            opacity: isHovered ? 1 : 0.5
                                        }}
                                    >
                                        {isAddAreaGroupSelected ? (
                                            <IC variant={'custom'} size={36}>
                                                <LevelElevatorPolygon />
                                            </IC>
                                        ) : (
                                            <IC variant={'dark'} size={36} iconColor={color}>
                                                <EnterOffice />
                                            </IC>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </Panel>

            <Dialog hidden={modalIsOpen} onDismiss={onDismiss} dialogContentProps={dialogContentProps} modalProps={modelProps}>
                <DialogFooter aria-label="delete-area-dialog-footer">
                    <DefaultButton text={IvicosStrings.cancelActionTitle} onClick={onDismiss} />
                    <PrimaryButton
                        text={deleteAreaOrAreaGroup.isAreaGroup ? IvicosStrings.deleteAreaGroupActionTitle : IvicosStrings.deleteAreaActionTitle}
                        onClick={() => {
                            deleteAreaHandler(deleteAreaOrAreaGroup);
                            setModalIsOpen(true);
                        }}
                        aria-label="delete-area-primary-button"
                    />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default AreaListView;
